import { useRef, useEffect } from 'react';
import { cn } from '@/lib/cn';

interface NavDropdownProps {
  open: boolean;
  onClose: () => void;
  trigger: React.ReactNode;
  onTriggerClick: () => void;
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
  closeOnContentClick?: boolean;
}

export function NavDropdown({
  open,
  onClose,
  trigger,
  onTriggerClick,
  children,
  align = 'left',
  className,
  closeOnContentClick = true,
}: NavDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={onTriggerClick}
        className={cn(
          'flex items-center gap-1.5 rounded-lg border border-transparent px-2 py-1.5 transition-colors',
          open
            ? 'border-primary shadow-sm shadow-primary'
            : 'hover:border-border hover:shadow-sm',
        )}
      >
        {trigger}
      </button>
      {open && (
        <div
          className={cn(
            'absolute top-full z-50 mt-2 min-w-[220px] rounded-xl border border-border/60 bg-card py-1 shadow-xl',
            align === 'right' ? 'right-0' : 'left-0',
            className,
          )}
          onClick={closeOnContentClick ? onClose : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}
